import { Box, Button } from '@material-ui/core';
import { useAuth } from '@nemuru-eng/providers';
import React from 'react';
import { Link } from 'react-router-dom';

import { Font } from '@/components/shared/components/Font';
import { IconWithCircle } from '@/components/shared/components/IconWithCircle';
import { ProfileAvatar } from '@/components/shared/components/ProfileAvatar';
import { ProfileSwitcherMenu } from '@/components/shared/components/ProfileSwitcherMenu';
import { Text } from '@/components/shared/components/Text';
import { APP_ROLES, ICON_NAMES } from '@/config/config';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { useUIWrapper } from '@/hooks/useUIWrapper';
import { paths } from '@/Routes';

const overflowEllipsisStyle = {
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as React.CSSProperties;

export const ROLE_ICON_NAMES = {
  [APP_ROLES.SUPER_ADMIN]: ICON_NAMES.SUPER_ADMIN,
  [APP_ROLES.ADMIN]: ICON_NAMES.ADMIN,
  [APP_ROLES.LENDER]: ICON_NAMES.LENDER,
  [APP_ROLES.MERCHANT]: ICON_NAMES.MERCHANT,
  [APP_ROLES.CHANNEL]: ICON_NAMES.CHANNEL,
  [APP_ROLES.AGENT]: ICON_NAMES.AGENT,
  [APP_ROLES.MERCHANT_GROUP]: ICON_NAMES.PARTNER,
  [APP_ROLES.PARTNER]: ICON_NAMES.PARTNER, // TODO switch case
  [APP_ROLES.THIRD_PARTY_VALIDATION]: ICON_NAMES.THIRD_PARTY_VALIDATION,
  [APP_ROLES.CPC_BACK_OFFICE]: ICON_NAMES.CPC_BACK_OFFICE,
};

export function AccountButton({ disabled }: { disabled: boolean }) {
  const { userEntity } = useUserContext();
  const { palette, customBorderRadius } = useCustomTheme();
  const { leftMenuWidth } = useUIWrapper();
  const { md, sm } = useScreen();
  const { activeProfile, profiles } = useAuth();
  const hasSeveralProfiles = !!profiles && profiles.length >= 2;
  const otherProfilesCount = hasSeveralProfiles ? profiles.length - 1 : 0;

  if (!userEntity) return null;

  const button = (
    <Button
      variant="text"
      style={{
        padding: '0 15px 0 8px ',
        margin: '20px 0 15px -8px',
        height: '56px',
        width: md ? 'calc(100% + 16px)' : sm ? 'max-content' : 'calc(100% - 55px)',
        minWidth: !md && sm ? `calc(${leftMenuWidth} + 68px)` : 'unset',
        maxWidth: md ? 'unset' : 'calc(100vw - 50px)',
        borderRadius: customBorderRadius.chip,
      }}
      disabled={hasSeveralProfiles ? false : disabled}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" width={'100%'}>
        <div
          style={{
            flexShrink: 0,
            minWidth: '40px',
            color: palette.primary.contrastText,
            fontSize: 25,
            fontWeight: '100',
          }}
        >
          {activeProfile ? (
            <ProfileAvatar profile={activeProfile} />
          ) : (
            <IconWithCircle
              className="material-icons-outlined"
              height={40}
              width={40}
              bgcolor={palette.primary.main}
              borderRadius={customBorderRadius.chip}
              iconProps={{
                style: {
                  color: palette.primary.contrastText,
                  fontSize: 25,
                  fontWeight: '100',
                  flexShrink: 'none',
                },
              }}
            >
              {ROLE_ICON_NAMES[userEntity.role]}
            </IconWithCircle>
          )}
        </div>
        <Box
          display={'flex'}
          flex={'0.92'}
          flexDirection={'column'}
          alignItems="flex-start"
          paddingLeft={'10px'}
          maxWidth={md ? `calc(100% - ${disabled ? '42px' : '82px'})` : 'unset'}
        >
          <Font
            variant="h5"
            color="title"
            style={{
              lineHeight: '18px',
              marginTop: '4px',
              ...overflowEllipsisStyle,
            }}
          >
            {activeProfile?.displayName ?? userEntity.name}
          </Font>
          <Font variant="subtitle2" color="textSecondary" style={overflowEllipsisStyle}>
            <small>
              <Text tId={`user_${activeProfile?.userType ?? userEntity.user.userType}`} />
              {hasSeveralProfiles && (
                <>
                  {' · '}
                  <Text
                    tId={otherProfilesCount === 1 ? 'MORE_PROFILES_ONE' : 'MORE_PROFILES'}
                    dynamicDataTexts={[String(otherProfilesCount)]}
                  />
                </>
              )}
            </small>
          </Font>
        </Box>
        {!disabled && (
          <Box paddingLeft={md ? 2 : 3} display={'flex'} alignContent="center">
            <i className="material-icons-outlined">{hasSeveralProfiles ? 'unfold_more' : 'chevron_right'}</i>
          </Box>
        )}
      </Box>
    </Button>
  );

  return (
    <Box width={'100%'} borderTop={`1px solid ${palette.grey[300]}`}>
      {hasSeveralProfiles ? (
        <ProfileSwitcherMenu profiles={profiles} activeProfileId={activeProfile?.id} disabledAccountLink={disabled}>
          {button}
        </ProfileSwitcherMenu>
      ) : (
        <Link to={disabled ? '#' : paths.AccountView}>{button}</Link>
      )}
    </Box>
  );
}
